import type { UserRole } from './types'

/**
 * MASTER DATA ACCESS — BOARD RESOLUTION, 31 AUGUST 2026
 * =====================================================
 * This module is a transcription of the board's access matrix, and nothing
 * else. It is deliberately dull, verbose and repetitive: every role is written
 * out against every master, in the same order as the board's own sheet, so the
 * two can be read side by side by somebody who does not write software.
 *
 * There is no cleverness here on purpose. Deriving one role's rights from
 * another's - "Procurement Manager, same as Head of Procurement" - is how a
 * matrix and its implementation drift the first time the board changes one of
 * them.
 *
 * TWO LEVELS PER MASTER, NOT ONE
 * ------------------------------
 * The board distinguishes the LIST (the table of rows) from the DETAIL (opening
 * one row). They are not always the same: a Project Manager may see the list of
 * materials but not open one, and Accounts may see the list of sites but not
 * open one. So each master carries both.
 *
 *   CRUD  may open the row AND change it; the Add button appears
 *   VIEW  may look, never change
 *   NONE  the tab is not drawn at all / the row has no action
 *
 * THE ONE COLUMN-LEVEL RULE
 * -------------------------
 * "View - except base rate" for the Site Store-man, the Resident Construction
 * Manager and the Stores Executive. See maySeeBaseRate() at the foot.
 */

export type AccessLevel = 'CRUD' | 'VIEW' | 'NONE'

export const MASTER_TABS = [
  'materials', 'categories', 'uoms', 'vendors', 'sites', 'terms', 'settings',
] as const
export type MasterTab = typeof MASTER_TABS[number]

interface Access { list: AccessLevel; detail: AccessLevel }

const N: Access = { list: 'NONE', detail: 'NONE' }

/**
 * The matrix. Rows are the board's rows; columns are its columns.
 *
 * A role missing from a master's map has NO access to it - stated once, here,
 * rather than repeated as 'NONE' forty times.
 */
const MATRIX: Record<MasterTab, Partial<Record<UserRole, Access>>> = {
  // ---- Materials ---------------------------------------------------------
  // The three site-facing roles see the list without the base rate, and cannot
  // open a material at all.
  materials: {
    storeman:                  { list: 'VIEW', detail: 'NONE' },
    site_construction_manager: { list: 'VIEW', detail: 'NONE' },
    store_executive:           { list: 'VIEW', detail: 'NONE' },
    stores_head:               { list: 'CRUD', detail: 'CRUD' },
    procurement_manager:       { list: 'VIEW', detail: 'VIEW' },
    procurement_head:          { list: 'VIEW', detail: 'VIEW' },
    accounts_spoc:             { list: 'VIEW', detail: 'VIEW' },
    project_manager:           { list: 'VIEW', detail: 'NONE' },
    project_head:              { list: 'VIEW', detail: 'VIEW' },
    cmd:                       { list: 'VIEW', detail: 'VIEW' },
    cxo:                       { list: 'VIEW', detail: 'VIEW' },
    admin:                     { list: 'CRUD', detail: 'CRUD' },
  },

  // ---- Vendors -----------------------------------------------------------
  vendors: {
    storeman: N, site_construction_manager: N, store_executive: N,
    stores_head:               { list: 'VIEW', detail: 'VIEW' },
    procurement_manager:       { list: 'CRUD', detail: 'CRUD' },
    procurement_head:          { list: 'CRUD', detail: 'CRUD' },
    accounts_spoc:             { list: 'VIEW', detail: 'VIEW' },
    project_manager:           { list: 'VIEW', detail: 'NONE' },
    project_head:              { list: 'VIEW', detail: 'VIEW' },
    cmd:                       { list: 'VIEW', detail: 'VIEW' },
    cxo:                       { list: 'VIEW', detail: 'VIEW' },
    admin:                     { list: 'CRUD', detail: 'CRUD' },
  },

  // ---- Units of measure --------------------------------------------------
  uoms: {
    storeman: N, site_construction_manager: N, store_executive: N,
    stores_head:               { list: 'CRUD', detail: 'CRUD' },
    procurement_manager:       { list: 'CRUD', detail: 'CRUD' },
    procurement_head:          { list: 'CRUD', detail: 'CRUD' },
    accounts_spoc: N,
    project_manager: N,
    project_head:              { list: 'VIEW', detail: 'VIEW' },
    cmd:                       { list: 'VIEW', detail: 'VIEW' },
    cxo:                       { list: 'VIEW', detail: 'VIEW' },
    admin:                     { list: 'CRUD', detail: 'CRUD' },
  },

  // ---- Sites -------------------------------------------------------------
  // CRUD is the System Administrator's alone under this resolution. Procurement
  // dropped to View, which is a REMOVAL of a right they were using last week to
  // add the twenty-one new sites. Confirmed as intended, 31 Aug 2026.
  sites: {
    storeman: N, site_construction_manager: N, store_executive: N,
    stores_head:               { list: 'VIEW', detail: 'VIEW' },
    procurement_manager:       { list: 'VIEW', detail: 'VIEW' },
    procurement_head:          { list: 'VIEW', detail: 'VIEW' },
    accounts_spoc:             { list: 'VIEW', detail: 'NONE' },
    project_manager:           { list: 'VIEW', detail: 'NONE' },
    project_head:              { list: 'VIEW', detail: 'VIEW' },
    cmd:                       { list: 'VIEW', detail: 'VIEW' },
    cxo:                       { list: 'VIEW', detail: 'VIEW' },
    admin:                     { list: 'CRUD', detail: 'CRUD' },
  },

  // ---- Purchase-order terms ----------------------------------------------
  terms: {
    storeman: N, site_construction_manager: N, store_executive: N,
    stores_head:               { list: 'VIEW', detail: 'VIEW' },
    procurement_manager:       { list: 'CRUD', detail: 'CRUD' },
    procurement_head:          { list: 'CRUD', detail: 'CRUD' },
    accounts_spoc: N,
    project_manager: N,
    project_head:              { list: 'VIEW', detail: 'VIEW' },
    cmd:                       { list: 'VIEW', detail: 'VIEW' },
    cxo:                       { list: 'VIEW', detail: 'VIEW' },
    admin:                     { list: 'CRUD', detail: 'CRUD' },
  },

  // ---- Categories --------------------------------------------------------
  // Not in the resolution. Ruled 31 Aug 2026: it follows Materials and Vendors,
  // because it is the list behind the Category boxes on both of those forms -
  // whoever MAINTAINS either master must be able to add a sub-category while
  // creating a row, or every new material becomes a request to somebody else.
  //
  // It follows the CRUD holders only, NOT the viewers. The reasoning above is
  // about maintaining a master; somebody who may merely look at the material
  // list has no use for the taxonomy behind a form he cannot open. Following
  // the viewers as well would have handed the Site Store-man a Categories tab
  // the board never mentioned - which the tests caught.
  //
  // Computed rather than written out, and the ONLY derived row in this file,
  // so the derivation is visible instead of being spread across twelve lines
  // that could quietly disagree with the two masters they follow.
  categories: {},

  // ---- Settings ----------------------------------------------------------
  // Not in the resolution. Unchanged: the System Administrator alone.
  settings: {
    admin: { list: 'CRUD', detail: 'CRUD' },
  },
}

const STRONGEST = (a: AccessLevel, b: AccessLevel): AccessLevel =>
  a === 'CRUD' || b === 'CRUD' ? 'CRUD'
    : a === 'VIEW' || b === 'VIEW' ? 'VIEW'
      : 'NONE'

function raw(role: UserRole, tab: MasterTab): Access {
  if (tab === 'categories') {
    const m = MATRIX.materials[role] ?? N
    const v = MATRIX.vendors[role] ?? N
    const may = m.list === 'CRUD' || v.list === 'CRUD'
    return may ? { list: 'CRUD', detail: 'CRUD' } : N
  }
  return MATRIX[tab][role] ?? N
}

/** May this role see the TABLE for this master, and may it add a row? */
export function masterListAccess(role: UserRole, tab: MasterTab): AccessLevel {
  return raw(role, tab).list
}

/** May this role OPEN a row, and may it change what it finds? */
export function masterDetailAccess(role: UserRole, tab: MasterTab): AccessLevel {
  return raw(role, tab).detail
}

/** The tabs to draw, in the screen's own order. Empty means refuse the page. */
export function visibleMasterTabs(role: UserRole): MasterTab[] {
  return MASTER_TABS.filter((t) => masterListAccess(role, t) !== 'NONE')
}

/**
 * "View - except base rate".
 *
 * The three site-facing roles see the material list without what the company
 * pays. They order and count materials; they do not price them, and a rate on
 * a screen at a site is a rate that can be quoted to a supplier.
 *
 * This hides the column. The value still reaches the browser, exactly as the
 * valued stock figures already do elsewhere in the application - confirmed as
 * sufficient on 31 Aug 2026. It is a screen control, not a data control.
 */
export function maySeeBaseRate(role: UserRole): boolean {
  return !['storeman', 'site_construction_manager', 'store_executive'].includes(role)
}
